/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosRequestConfig, Method } from "axios";
import { executeRequest } from "_foundation/axios/axiosConfig";
import { getSite } from "_foundation/hooks/useSite";
import cartService from "_foundation/apis/transaction/cart.service";
import {
  CartApprovalApi,
  XCartApi,
} from "@efood-commerce-sdk/typescript-axios-transaction-efood";
import { site as constantsSite } from "_foundationExt/constants/site";
import addOfflineAwareRequestHeaders from "pwa/offline/addOfflineAwareRequestHeaders";
import { Resource } from "pwa/offline";
import { CartApi } from "@hcl-commerce-store-sdk/typescript-axios-transaction";

const cartApprovalApiInstance = new CartApprovalApi(
  undefined,
  constantsSite.transactionContext
);

const cartApiInstance = new CartApi(
  undefined,
  constantsSite.transactionContext
);

const xCartApiInstance = new XCartApi(
  undefined,
  constantsSite.transactionContext
);

const cartServiceExt = {
  /**
   * Gets order details for the shopping cart.
   * `@method`
   * `@name Cart#getCart`
   *
   * `@param {any} parameters` have following properties:
   ** `@property {string} storeId (required)` The child property of `Parameters`.The store identifier.
   ** `@property {string} responseFormat ` The response format. If the request has an input body, that body must also use the format specified in "responseFormat". Valid values include "json" and "xml" without the quotes. If the responseFormat isn't specified, the "accept" HTTP header shall be used to determine the format of the response. If the "accept" HTTP header isn't specified as well, the default response format shall be in json.
   ** `@property {integer} pageNumber ` Page number, starting at 1. Valid values include positive integers of 1 and above. The "pageSize" must be specified for paging to work.
   ** `@property {integer} pageSize ` Page size. Used to limit the amount of data returned by a query. Valid values include positive integers of 1 and above. The "pageNumber" must be specified for paging to work.
   ** `@property {string} currency ` The currency code to use. Example usage : currency=USD. If no currency code is specified, the store default currency shall be used.
   ** `@property {string} sortOrderItemBy ` The order the results are sorted by ie:orderItemID
   ** `@property {string} profileName ` Profile name. Profiles determine the subset of data to be returned by a query.
   */
  getCart(
    parameters?: AxiosRequestConfig & {
      storeId?: string;
      langId?: "-1" | "-3";
      refreshAvailabilities?: boolean;
      responseFormat?: "xml" | "json";
      pageNumber?: number;
      pageSize?: number;
      currency?: "EUR" | "USD";
      sortOrderItemBy?: string;
      profileName?: "IBM_Summary" | "IBM_Details";
    }
  ) {
    const siteInfo = getSite();
    const {
      storeId = siteInfo?.storeID,
      langId,
      refreshAvailabilities,
      responseFormat,
      pageNumber,
      pageSize,
      currency,
      sortOrderItemBy,
      profileName,
      ...options
    } = parameters ?? {};

    return xCartApiInstance.getCart(
      storeId!,
      langId,
      refreshAvailabilities,
      responseFormat,
      pageNumber,
      pageSize,
      currency,
      sortOrderItemBy,
      profileName,
      addOfflineAwareRequestHeaders(
        { resource: Resource.Cart, originMethod: "getCart" },
        options
      )
    );
  },

  setOrderComment(parameters: any, url?: string) {
    const param = parameters;
    const site = getSite();
    let siteContext = "";
    if (site) {
      siteContext = site.transactionContext || "";
    }
    const domain = url || siteContext;
    const path = "/store/{storeId}/cart/set_order_comment";
    let requestUrl = domain + path;
    const method: Method = "POST";
    let body = {};
    const header = {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    };
    const queryParameters = new URLSearchParams();

    if (param.storeId === undefined && site !== null) {
      param.storeId = site.storeID;
    }
    requestUrl = requestUrl.replace("{storeId}", param.storeId);

    if (param.storeId === undefined) {
      throw new Error(`Request '${path}' missing required parameter storeId`);
    }

    if (param.body !== undefined) {
      body = parameters.body;
    }

    if (parameters.body.orderId === undefined) {
      throw new Error(`Request '${path}' missing required parameter orderId`);
    }

    if (param.responseFormat !== undefined) {
      const name = "responseFormat";
      const parameter = param[name];
      delete param[name];
      if (parameter instanceof Array) {
        parameter.forEach((value) => {
          queryParameters.append(name, value);
        });
      } else {
        queryParameters.set(name, parameter);
      }
    }

    if (param.body !== undefined) {
      body = parameters.body;
    }

    if (param.mode !== undefined) {
      const name = "mode";
      const parameter = param[name];
      delete param[name];
      if (parameter instanceof Array) {
        parameter.forEach((value) => {
          queryParameters.append(name, value);
        });
      } else {
        queryParameters.set(name, parameter);
      }
    }

    if (param.$queryParameters) {
      Object.keys(param.$queryParameters).forEach((parameterName) => {
        const parameter = param.$queryParameters[parameterName];
        queryParameters.set(parameterName, parameter);
      });
    }

    queryParameters.sort();
    const requestOptions: AxiosRequestConfig = {
      params: queryParameters,
      method,
      headers: header,
      data: body,
      url: requestUrl,
      ...parameters,
    };

    return executeRequest(requestOptions);
  },

  createOrder(parameters: any, url?: string) {
    const param = parameters;
    const site = getSite();
    let siteContext = "";
    if (site) {
      siteContext = site.transactionContext || "";
    }
    const domain = url || siteContext;
    const path = "/store/{storeId}/cart/create_order";
    let requestUrl = domain + path;
    const method: Method = "POST";
    let body = {};
    const header = {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    };
    const queryParameters = new URLSearchParams();

    if (param.storeId === undefined && site !== null) {
      param.storeId = site.storeID;
    }
    requestUrl = requestUrl.replace("{storeId}", param.storeId);

    if (param.storeId === undefined) {
      throw new Error(`Request '${path}' missing required parameter storeId`);
    }

    if (param.body !== undefined) {
      body = parameters.body;
    }

    if (param.description !== undefined) {
      const name = "description";
      const parameter = param[name];
      if (parameter instanceof Array) {
        parameter.forEach((value) => {
          queryParameters.append(name, value);
        });
      } else {
        queryParameters.set(name, parameter);
      }
    }

    if (param.$queryParameters) {
      Object.keys(param.$queryParameters).forEach((parameterName) => {
        const parameter = param.$queryParameters[parameterName];
        queryParameters.set(parameterName, parameter);
      });
    }

    queryParameters.sort();
    const requestOptions: AxiosRequestConfig = {
      params: queryParameters,
      method,
      headers: header,
      data: body,
      url: requestUrl,
      ...parameters,
    };

    return executeRequest(requestOptions);
  },

  emptyCart(parameters?: AxiosRequestConfig & { storeId?: string }) {
    const siteInfo = getSite();
    const { storeId = siteInfo?.storeID, ...options } = parameters ?? {};

    return cartApiInstance.cartCancelOrderInCart(
      storeId!,
      undefined,
      addOfflineAwareRequestHeaders(
        { resource: Resource.Cart, originMethod: "emptyCart" },
        options
      )
    );
  },

  checkApproval(orderId: string) {
    const siteInfo = getSite();
    if (!siteInfo) {
      return Promise.reject();
    }

    return cartApprovalApiInstance.checkApproval(
      siteInfo?.storeID,
      orderId,
      addOfflineAwareRequestHeaders({
        resource: Resource.Cart,
        originMethod: "checkApproval",
      })
    );
  },

  updateRewardChoice(parameters: any, url?: string) {
    const param = parameters;
    const site = getSite();
    let siteContext = "";
    if (site) {
      siteContext = site.transactionContext || "";
    }
    const domain = url || siteContext;
    const path = "/store/{storeId}/cart/@self/update_reward_option";
    let requestUrl = domain + path;
    const method: Method = "PUT";
    let body = {};
    const header = {
      Accept: "application/json",
      "Content-Type": "application/json; charset=utf-8",
    };
    const queryParameters = new URLSearchParams();

    if (param.storeId === undefined && site !== null) {
      param.storeId = site.storeID;
    }
    requestUrl = requestUrl.replace("{storeId}", param.storeId);

    if (param.storeId === undefined) {
      throw new Error(`Request '${path}' missing required parameter storeId`);
    }

    if (param.body !== undefined) {
      body = parameters.body;
    }

    if (param.body.orderId === undefined) {
      throw new Error(`Request '${path}' missing required parameter orderId`);
    }

    if (param.body.rewardOptionId === undefined) {
      throw new Error(
        `Request '${path}' missing required parameter rewardOptionId`
      );
    }

    if (param.responseFormat !== undefined) {
      const name = "responseFormat";
      const parameter = param[name];
      delete param[name];
      if (parameter instanceof Array) {
        parameter.forEach((value) => {
          queryParameters.append(name, value);
        });
      } else {
        queryParameters.set(name, parameter);
      }
    }

    if (param.mode !== undefined) {
      const name = "mode";
      const parameter = param[name];
      delete param[name];
      if (parameter instanceof Array) {
        parameter.forEach((value) => {
          queryParameters.append(name, value);
        });
      } else {
        queryParameters.set(name, parameter);
      }
    }

    const requestOptions: AxiosRequestConfig = {
      params: queryParameters,
      method,
      headers: header,
      data: body,
      url: requestUrl,
      ...parameters,
    };

    return executeRequest(requestOptions);
  },
};

export default { ...cartService, ...cartServiceExt };
